import { useState, useMemo, useCallback } from 'react';
import {
  type Module,
  type ModuleStatus,
  type ModuleVisibility,
  STATUS_ORDER,
} from '@/shared/schemas';
import type { ModuleFilters } from '@/shared/types';

interface UseModuleFiltersResult {
  readonly filters: ModuleFilters;
  readonly filteredModules: readonly Module[];
  readonly setStatus: (status: ModuleStatus | 'all') => void;
  readonly setVisibility: (visibility: ModuleVisibility | 'all') => void;
  readonly setSearch: (search: string) => void;
  readonly reset: () => void;
}

const DEFAULT_FILTERS: ModuleFilters = {
  status: 'all',
  visibility: 'all',
  search: '',
};

/**
 * Hook to manage module filtering and search.
 * Provides filtered and sorted modules based on current filter state.
 */
export function useModuleFilters(modules: readonly Module[]): UseModuleFiltersResult {
  const [filters, setFilters] = useState<ModuleFilters>(DEFAULT_FILTERS);

  const setStatus = useCallback((status: ModuleStatus | 'all') => {
    setFilters((prev) => ({ ...prev, status }));
  }, []);

  const setVisibility = useCallback((visibility: ModuleVisibility | 'all') => {
    setFilters((prev) => ({ ...prev, visibility }));
  }, []);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search }));
  }, []);

  const reset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const filteredModules = useMemo(() => {
    const searchLower = filters.search.toLowerCase().trim();

    const filtered = modules.filter((module) => {
      // Status filter
      if (filters.status !== 'all' && module.status !== filters.status) {
        return false;
      }

      // Visibility filter
      if (filters.visibility !== 'all' && module.visibility !== filters.visibility) {
        return false;
      }

      // Search filter
      if (searchLower) {
        const matchesName = module.name.toLowerCase().includes(searchLower);
        const matchesDescription = module.description.toLowerCase().includes(searchLower);
        const matchesTags = module.tags.some((tag) => tag.toLowerCase().includes(searchLower));

        if (!matchesName && !matchesDescription && !matchesTags) {
          return false;
        }
      }

      return true;
    });

    // Sort by status: active first, then alpha, then archive
    return [...filtered].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  }, [modules, filters]);

  return {
    filters,
    filteredModules,
    setStatus,
    setVisibility,
    setSearch,
    reset,
  };
}
